import React from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import {CollapsiblePanel} from 'entities/tariff';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import LabelWithHint from 'shared/ui/FormGroup/LabelWithHint/LabelWithHint';
import InputDomain from 'shared/ui/Input/InputDomain';
import InputText from 'shared/ui/Input/InputText';
import {TagsChipField} from 'entities/monitor';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import {NotifyChannels} from 'entities/notification';
import IDomainMonitorOutput from 'features/domain/model/DomainMonitor/IDomainMonitor';
import useDomainMonitorForm from 'features/domain/model/useDomainMonitorForm';

import DomainMonitorChecks from './DomainMonitorChecks/DomainMonitorChecks';

import styles from './DomainMonitorForm.module.scss';

interface IDomainMonitorFormProps {
    item?: IDomainMonitorOutput,
    onSuccess: () => void,
    onLimitExceeded?: () => void,
    onClose?: () => void,
}

const DomainMonitorForm: React.FC<IDomainMonitorFormProps> = ({
    item,
    onSuccess,
    onLimitExceeded,
    onClose,
}) => {
    const {form, isNew, isSubmitting, freeTariff, submit} = useDomainMonitorForm({item, onSuccess, onLimitExceeded});
    const {register, formState, control} = form;
    const {errors, isValid} = formState;

    return (
        <form onSubmit={submit} className={styles.form}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    label={
                        isNew
                            ? undefined
                            : <LabelWithHint
                                label={t`Домен`}
                                hint={t`Домен монитора изменить нельзя. Создайте новый монитор для другого домена.`}
                            />
                    }
                    error={errors?.domain?.message}
                    className={cn(styles.formGroup, isNew && styles.firstFormGroup)}
                >
                    {
                        isNew
                            ? (
                                <InputDomain
                                    register={register}
                                    name="domain"
                                    placeholder="example.com"
                                    control={control}
                                />
                            )
                            : (
                                <InputText
                                    register={register}
                                    name="domain"
                                    control={control}
                                    defaultValue={item?.domain_human_readable}
                                    readOnly
                                    validate={false}
                                />
                            )
                    }
                </FormGroupWithError>

                <TagsChipField
                    control={control}
                    label={t`Теги`}
                    error={errors?.tags?.message}
                    className={styles.formGroup}
                />

                <FormGroup>
                    <Checkbox textStyle={styles.checkbox} {...register('active')}>
                        {t`Монитор активен`}
                    </Checkbox>
                </FormGroup>
            </ModalBodyWrapper>

            <ModalBodyWrapper>
                <CollapsiblePanel
                    title={t`Проверки`}
                    defaultExpanded
                    className={styles.panel}
                    isFreeTariff={freeTariff}
                    premiumCondition="monitor_ssl_enabled"
                >
                    <DomainMonitorChecks register={register}/>
                </CollapsiblePanel>
            </ModalBodyWrapper>

            <ModalBodyWrapper>
                <CollapsiblePanel
                    title={t`Уведомления`}
                    defaultExpanded={false}
                    className={styles.panel}
                >
                    <NotifyChannels
                        control={control}
                        register={register}
                        emailFieldName="notify_by_email"
                        additionalEmailsFieldName="additional_emails"
                        telegramFieldName="telegram_chats"
                        maxFieldName="max_chats"
                    />
                </CollapsiblePanel>
            </ModalBodyWrapper>

            <ModalBlockButton
                primaryButtonText={isNew ? t`Добавить` : t`Сохранить`}
                primaryButtonType="submit"
                primaryButtonLoading={isSubmitting}
                primaryButtonDisabled={!isValid}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default DomainMonitorForm;
